import React, { createContext, useContext, useState } from 'react'

export interface UserSettings {
  editable: boolean
  theme_color: string
  chat: boolean
  inputs: {
    camera: boolean
    screen: boolean
    mic: boolean
  }
  outputs: {
    audio: boolean
    video: boolean
  }
  ws_url: string
  token: string
  room_name: string
  participant_id: string
  participant_name: string
  agent_name?: string
  metadata?: string
  attributes?: Array<{ id: string; key: string; value: string }>
  simulcast?: boolean
  dynacast?: boolean
  adaptiveStream?: boolean
  videoCodec?: string
  audioCodec?: string
}

export interface AppConfig {
  title: string
  description: string
  github_link?: string
  video_fit?: 'cover' | 'contain'
  settings: UserSettings
  show_qr?: boolean
}

const defaultConfig: AppConfig = {
  title: 'Agents Playground',
  description: 'A virtual workbench for testing multimodal AI agents.',
  video_fit: 'contain',
  settings: {
    editable: true,
    theme_color: 'cyan',
    chat: true,
    inputs: {
      camera: true,
      screen: true,
      mic: true,
    },
    outputs: {
      audio: true,
      video: true,
    },
    ws_url: '',
    token: '',
    room_name: '',
    participant_id: '',
    participant_name: '',
    metadata: '',
    attributes: [],
    simulcast: true,
    dynacast: true,
    adaptiveStream: true,
    videoCodec: 'vp8',
    audioCodec: 'opus',
  },
  show_qr: false,
}

type ConfigData = {
  config: AppConfig
  setUserSettings: (settings: UserSettings) => void
}

const ConfigContext = createContext<ConfigData | undefined>(undefined)

export const ConfigProvider = ({
  children,
  livekitUrl,
}: {
  children: React.ReactNode
  livekitUrl?: string
}) => {
  const [config, setConfig] = useState<AppConfig>({
    ...defaultConfig,
    settings: {
      ...defaultConfig.settings,
      ws_url: livekitUrl || '',
    },
  })

  const setUserSettings = (settings: UserSettings) => {
    setConfig(prev => ({
      ...prev,
      settings,
    }))
  }

  return (
    <ConfigContext.Provider value={{ config, setUserSettings }}>
      {children}
    </ConfigContext.Provider>
  )
}

export const useConfig = () => {
  const context = useContext(ConfigContext)
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider')
  }
  return context
}
